import { create } from 'zustand';
import type { Settings } from '../types';
import { captureSessionEpoch, isSessionEpochCurrent } from '../platform/sessionEpoch';

interface SettingsState {
  settings: Settings | null;
  isLoading: boolean;
  loadSettings: () => Promise<void>;
  updateSettings: (settings: Partial<Settings>) => Promise<void>;
  reset: () => void;
}

const DEFAULT_SETTINGS: Settings = {
  providers: {
    mock: {
      enabled: true,
      apiKey: '',
    },
    'nano-banana': {
      enabled: true,
      apiKey: '',
      baseUrl: 'https://api3.wlai.vip',
      model: 'gemini-3.1-flash-image',
      timeout: 240000,
    },
  },
  defaultProvider: 'mock',
  defaultWidth: 1024,
  defaultHeight: 1024,
  defaultQuality: 'standard',
  namingTemplate: '{task_id}_{role}',
  language: 'zh-CN',
};

let settingsLoadSequence = 0;
let settingsEpoch = 0;

function captureSettingsEpoch() {
  return { store: settingsEpoch, session: captureSessionEpoch() };
}

function isSettingsEpochCurrent(epoch: ReturnType<typeof captureSettingsEpoch>): boolean {
  return epoch.store === settingsEpoch && isSessionEpochCurrent(epoch.session);
}

export const useSettingsStore = create<SettingsState>((set) => ({
  settings: null,
  isLoading: false,

  loadSettings: async () => {
    const epoch = captureSettingsEpoch();
    const requestSequence = ++settingsLoadSequence;
    set({ isLoading: true });
    try {
      const settings = await window.electron.invoke(window.IPC_CHANNELS.SETTINGS_GET_ALL);
      if (requestSequence !== settingsLoadSequence || !isSettingsEpochCurrent(epoch)) return;
      set({ settings: mergeSettings(settings), isLoading: false });
    } catch (error) {
      console.error('Failed to load settings:', error);
      if (requestSequence === settingsLoadSequence && isSettingsEpochCurrent(epoch)) {
        set({ settings: DEFAULT_SETTINGS, isLoading: false });
      }
    }
  },

  updateSettings: async (partial) => {
    const epoch = captureSettingsEpoch();
    const settings = await window.electron.invoke(window.IPC_CHANNELS.SETTINGS_GET_ALL);
    const merged = mergeSettings({ ...settings, ...partial });

    for (const [key, value] of Object.entries(partial)) {
      await window.electron.invoke(window.IPC_CHANNELS.SETTINGS_SET, {
        key,
        value: JSON.stringify(value),
      });
    }

    if (isSettingsEpochCurrent(epoch)) set({ settings: merged });
  },

  reset: () => {
    ++settingsEpoch;
    ++settingsLoadSequence;
    set({ settings: null, isLoading: false });
  },
}));

function mergeSettings(settings: Partial<Settings>): Settings {
  const providers = { ...DEFAULT_SETTINGS.providers };
  for (const [name, provider] of Object.entries(settings.providers || {})) {
    if (name === 'yunwu') continue;
    if (!provider) continue;
    const defaults = DEFAULT_SETTINGS.providers[name] || {};
    providers[name] = {
      ...defaults,
      ...provider,
      apiKey: provider.apiKey || defaults.apiKey || '',
    };
  }

  const defaultProvider = settings.defaultProvider && providers[settings.defaultProvider]
    ? settings.defaultProvider
    : DEFAULT_SETTINGS.defaultProvider;
  return {
    ...DEFAULT_SETTINGS,
    ...settings,
    providers,
    defaultProvider,
  };
}
